import {
  IExtractorVendorMapping,
  IExtractorVendorMappingForm,
  IExtractorVendorMappingFormContact,
  ISaveExtractorVendorMappingPayload,
} from 'companySettings/types';
import { IContactGroupAddress } from 'contacts/types';

// `postForm` drops `undefined`/`null`, and the BE reads an absent key as "leave this field
// untouched" but an empty string as "clear it to NULL". A cleared vendor/contact MUST reach the
// wire as `''` — coalescing to `undefined` would silently keep the previous value.
const toClearableId = (id?: number | null): number | '' => id ?? '';

// The mapping stores the CRM `contact.id`, not the `corporate_user.id` the quote's `contact_id`
// holds — the two live in different id spaces and the vendor-contact autocomplete exposes both.
const toContactId = (form?: IExtractorVendorMappingForm | null): number | '' =>
  toClearableId(form?.contact?.contactId);

const toVendorId = (form?: IExtractorVendorMappingForm | null): number | '' =>
  toClearableId(form?.vendorLocation?.id);

// The single write endpoint decides create vs edit from `id` alone, so it rides the payload only
// when the drawer opened on an existing record — never as the `''` the two targets use, which would
// take the update branch with no row to update. `extractorId` is immutable once mapped, so it goes
// out only on the create branch: the update branch cannot act on it.
//
// The BE re-validates every target it receives, so an edit sends only the targets that differ from
// `stored`: re-sending an unchanged one that has since gone stale (deleted contact, vendor lost its
// AP) would 422 a save that only touched the other field.
export const mapExtractorVendorMappingToSavePayload = (
  form?: IExtractorVendorMappingForm | null,
  stored?: IExtractorVendorMapping | null
): ISaveExtractorVendorMappingPayload => {
  const contactGroupAddressId = toVendorId(form);
  const contactId = toContactId(form);
  const isUnchanged = (value: number | '', storedId?: number | null) =>
    Boolean(form?.id && stored) && value === toClearableId(storedId);

  return {
    ...(form?.id ? { id: form.id } : { extractorId: form?.extractorId?.trim() ?? '' }),
    ...(!isUnchanged(contactGroupAddressId, stored?.vendor?.contactGroupAddressId) && {
      contactGroupAddressId,
    }),
    ...(!isUnchanged(contactId, stored?.vendorContact?.contactId) && { contactId }),
  };
};

/**
 * The vendor picker's own option already carries the Partfiniti contact its supplier email resolves
 * to, so picking a vendor prefills the contact without a request.
 *
 * `primaryContact.contactId` is the ERP supplier contact id and is NOT what the mapping stores —
 * `partfinitiContactId` is. A null one means nothing resolved (`corporateUserId` is null with it),
 * which is no prefill rather than an unusable one.
 */
export const mapVendorLocationToFormContact = (
  vendorLocation?: Partial<IContactGroupAddress> | null
): IExtractorVendorMappingFormContact | null => {
  const primaryContact = vendorLocation?.primaryContact;

  if (!primaryContact?.partfinitiContactId) {
    return null;
  }

  return {
    contactId: primaryContact.partfinitiContactId,
    corporateUserId: primaryContact.corporateUserId ?? null,
    name: primaryContact.contactName,
    companyName: primaryContact.companyName ?? '',
  };
};

export const mapExtractorVendorMappingToForm = (
  mapping?: IExtractorVendorMapping | null
): IExtractorVendorMappingForm => ({
  id: mapping?.id,
  extractorId: mapping?.extractorId ?? '',
  // The picker lists locations by their own name and recovers its search query from the label it
  // built, so prefilling the parent vendor's name would make the field search for a location by its
  // parent and miss the row it is displaying. The group name stays the fallback for a location with
  // no ERP supplier-site link.
  vendorLocation: mapping?.vendor
    ? {
        id: mapping.vendor.contactGroupAddressId,
        name: mapping.vendor.vendorLocationName || mapping.vendor.name,
        accountsPayableId: mapping.vendor.accountsPayableId,
      }
    : null,
  contact: mapping?.vendorContact
    ? {
        contactId: mapping.vendorContact.contactId,
        corporateUserId: mapping.vendorContact.corporateUserId,
        name: mapping.vendorContact.name,
        companyName: mapping.vendorContact.companyName ?? '',
      }
    : null,
});
